import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import MetricCard from './components/MetricCard';
import EngagementChart from './components/EngagementChart';
import UserActivityTable from './components/UserActivityTable';
import ContentAnalytics from './components/ContentAnalytics';
import TimeRangeFilter from './components/TimeRangeFilter';
import CohortAnalysis from './components/CohortAnalysis';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { RealtimeSubscriptionService } from '../../services/realtimeSubscriptionService';
import { supabase } from '../../lib/supabase';

const AnalyticsDashboard = () => {
  const [selectedRange, setSelectedRange] = useState('30days');
  const [isLoading, setIsLoading] = useState(false);
  const [realtimeService] = useState(() => new RealtimeSubscriptionService());
  const [liveNotifications, setLiveNotifications] = useState([]);

  const mockUser = {
    name: "Admin User",
    email: "admin@bardplay.com",
    role: "admin"
  };

  const mockNotifications = [
    {
      id: 1,
      type: "info",
      title: "New Analytics Report",
      message: "Monthly analytics report is now available",
      timestamp: new Date(Date.now() - 3600000),
      read: false
    }
  ];

  // Set up real-time subscriptions for analytics updates
  useEffect(() => {
    const unsubscribe = realtimeService?.subscribeToStudentActivity(async (update) => {
      console.log('Real-time analytics update:', update);
      
      // Fetch user details for the notification
      let userName = 'A student';
      
      if (update?.data?.user_id) {
        const { data: userProfile } = await supabase
          ?.from('user_profiles')
          ?.select('full_name, email')
          ?.eq('id', update?.data?.user_id)
          ?.single();
        
        if (userProfile) {
          userName = userProfile?.full_name || userProfile?.email;
        }
      }
      
      // Create notification based on activity type
      let notification = {
        id: `live-${Date.now()}-${Math.random()}`,
        timestamp: update?.timestamp || new Date(),
        read: false
      };
      
      if (update?.type === 'badge_earned') {
        notification = {
          ...notification,
          type: 'success',
          title: 'Badge Earned',
          message: `${userName} earned a new badge`
        };
      } else if (update?.type === 'achievement_unlocked') {
        notification = {
          ...notification,
          type: 'success',
          title: 'Achievement Unlocked',
          message: `${userName} unlocked a new achievement`
        };
      } else if (update?.type === 'quiz_completed') {
        const score = update?.data?.score || 0;
        const totalQuestions = update?.data?.total_questions || 0;
        const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
        
        notification = {
          ...notification,
          type: 'info',
          title: 'Quiz Completed',
          message: `${userName} completed a quiz (${percentage}% score)`
        };
      }
      
      // Add to live notifications
      setLiveNotifications(prev => [notification, ...prev]?.slice(0, 10));
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [realtimeService]);

  // Combine live notifications with mock notifications
  const allNotifications = [...liveNotifications, ...mockNotifications];

  const kpiMetrics = [
    {
      title: "Total User Logins",
      value: "12,847",
      change: "+18.2%",
      changeType: "increase",
      icon: "Users",
      iconColor: "bg-primary",
      trend: true
    },
    {
      title: "Quiz Completion Rate",
      value: "87.3%",
      change: "+5.4%",
      changeType: "increase",
      icon: "CheckCircle",
      iconColor: "bg-success",
      trend: true
    },
    {
      title: "Avg Time on Site",
      value: "42m",
      change: "+12m",
      changeType: "increase",
      icon: "Clock",
      iconColor: "bg-accent",
      trend: true
    },
    {
      title: "Total XP Earned",
      value: "2.4M",
      change: "+23.7%",
      changeType: "increase",
      icon: "Zap",
      iconColor: "bg-secondary",
      trend: true
    }
  ];

  const engagementData = [
    { name: "Mon", logins: 245, quizzes: 189, interactions: 567 },
    { name: "Tue", logins: 312, quizzes: 234, interactions: 689 },
    { name: "Wed", logins: 289, quizzes: 198, interactions: 612 },
    { name: "Thu", logins: 356, quizzes: 267, interactions: 734 },
    { name: "Fri", logins: 398, quizzes: 312, interactions: 823 },
    { name: "Sat", logins: 178, quizzes: 134, interactions: 456 },
    { name: "Sun", logins: 156, quizzes: 112, interactions: 389 }
  ];

  const xpProgressData = [
    { name: "Week 1", xp: 45000 },
    { name: "Week 2", xp: 52000 },
    { name: "Week 3", xp: 48000 },
    { name: "Week 4", xp: 61000 }
  ];

  const userData = [
    {
      id: 1,
      name: "Emma Thompson",
      email: "emma.thompson@email.com",
      lastLogin: new Date(Date.now() - 86400000),
      totalLogins: 47,
      timeOnSite: 1847,
      quizzesCompleted: 23,
      xpEarned: 12450,
      engagementScore: 92
    },
    {
      id: 2,
      name: "James Wilson",
      email: "james.wilson@email.com",
      lastLogin: new Date(Date.now() - 172800000),
      totalLogins: 38,
      timeOnSite: 1523,
      quizzesCompleted: 19,
      xpEarned: 9870,
      engagementScore: 85
    },
    {
      id: 3,
      name: "Sophia Martinez",
      email: "sophia.martinez@email.com",
      lastLogin: new Date(Date.now() - 259200000),
      totalLogins: 52,
      timeOnSite: 2134,
      quizzesCompleted: 28,
      xpEarned: 15230,
      engagementScore: 94
    },
    {
      id: 4,
      name: "Oliver Brown",
      email: "oliver.brown@email.com",
      lastLogin: new Date(Date.now() - 345600000),
      totalLogins: 31,
      timeOnSite: 1289,
      quizzesCompleted: 16,
      xpEarned: 8340,
      engagementScore: 78
    },
    {
      id: 5,
      name: "Isabella Davis",
      email: "isabella.davis@email.com",
      lastLogin: new Date(Date.now() - 432000000),
      totalLogins: 44,
      timeOnSite: 1756,
      quizzesCompleted: 21,
      xpEarned: 11290,
      engagementScore: 88
    },
    {
      id: 6,
      name: "William Garcia",
      email: "william.garcia@email.com",
      lastLogin: new Date(Date.now() - 518400000),
      totalLogins: 29,
      timeOnSite: 1167,
      quizzesCompleted: 14,
      xpEarned: 7560,
      engagementScore: 72
    },
    {
      id: 7,
      name: "Ava Rodriguez",
      email: "ava.rodriguez@email.com",
      lastLogin: new Date(Date.now() - 604800000),
      totalLogins: 56,
      timeOnSite: 2345,
      quizzesCompleted: 31,
      xpEarned: 16780,
      engagementScore: 96
    },
    {
      id: 8,
      name: "Liam Anderson",
      email: "liam.anderson@email.com",
      lastLogin: new Date(Date.now() - 691200000),
      totalLogins: 35,
      timeOnSite: 1423,
      quizzesCompleted: 18,
      xpEarned: 9120,
      engagementScore: 81
    },
    {
      id: 9,
      name: "Mia Taylor",
      email: "mia.taylor@email.com",
      lastLogin: new Date(Date.now() - 777600000),
      totalLogins: 41,
      timeOnSite: 1678,
      quizzesCompleted: 22,
      xpEarned: 11890,
      engagementScore: 86
    },
    {
      id: 10,
      name: "Noah Thomas",
      email: "noah.thomas@email.com",
      lastLogin: new Date(Date.now() - 864000000),
      totalLogins: 27,
      timeOnSite: 1089,
      quizzesCompleted: 13,
      xpEarned: 6890,
      engagementScore: 69
    },
    {
      id: 11,
      name: "Charlotte Moore",
      email: "charlotte.moore@email.com",
      lastLogin: new Date(Date.now() - 950400000),
      totalLogins: 49,
      timeOnSite: 1967,
      quizzesCompleted: 26,
      xpEarned: 13670,
      engagementScore: 90
    },
    {
      id: 12,
      name: "Elijah Jackson",
      email: "elijah.jackson@email.com",
      lastLogin: new Date(Date.now() - 1036800000),
      totalLogins: 33,
      timeOnSite: 1345,
      quizzesCompleted: 17,
      xpEarned: 8790,
      engagementScore: 76
    }
  ];

  const popularPlays = [
    { id: 1, title: "Romeo and Juliet", interactions: 3456, popularity: 94 },
    { id: 2, title: "Hamlet", interactions: 2987, popularity: 87 },
    { id: 3, title: "Macbeth", interactions: 2654, popularity: 82 },
    { id: 4, title: "A Midsummer Night\'s Dream", interactions: 2341, popularity: 76 },
    { id: 5, title: "Othello", interactions: 1987, popularity: 68 }
  ];

  const quizPerformance = [
    { id: 1, title: "Romeo & Juliet - Act 1", completions: 234, avgScore: 87 },
    { id: 2, title: "Hamlet - Character Analysis", completions: 198, avgScore: 82 },
    { id: 3, title: "Macbeth - Pronunciation", completions: 176, avgScore: 75 },
    { id: 4, title: "Shakespeare\'s Language", completions: 156, avgScore: 68 }
  ];

  const glossaryTerms = [
    { id: 1, word: "wherefore", lookups: 1234 },
    { id: 2, word: "prithee", lookups: 987 },
    { id: 3, word: "forsooth", lookups: 876 },
    { id: 4, word: "anon", lookups: 765 },
    { id: 5, word: "hither", lookups: 654 }
  ];

  const cohortData = [
    { month: "Dec 2024", users: 145, week1: 87, week2: 76, week3: 68, week4: 62, avgEngagement: 73 },
    { month: "Nov 2024", users: 132, week1: 89, week2: 78, week3: 71, week4: 65, avgEngagement: 76 },
    { month: "Oct 2024", users: 128, week1: 85, week2: 74, week3: 66, week4: 59, avgEngagement: 71 },
    { month: "Sep 2024", users: 156, week1: 91, week2: 82, week3: 75, week4: 69, avgEngagement: 79 }
  ];

  const handleExport = () => {
    setIsLoading(true);
    setTimeout(() => {
      const csvContent = [
        ["Name", "Email", "Last Login", "Total Logins", "Time on Site", "Quizzes Completed", "XP Earned", "Engagement Score"],
        ...userData?.map(user => [
          user?.name,
          user?.email,
          new Date(user.lastLogin)?.toLocaleDateString(),
          user?.totalLogins,
          `${Math.floor(user?.timeOnSite / 60)}h ${user?.timeOnSite % 60}m`,
          user?.quizzesCompleted,
          user?.xpEarned,
          `${user?.engagementScore}%`
        ])
      ]?.map(row => row?.join(","))?.join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);
      setIsLoading(false);
    }, 1000);
  };

  const handleRangeChange = (range) => {
    setSelectedRange(range);
  };

  useEffect(() => {
    document.title = "Analytics Dashboard - BardPlay";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={mockUser}
        isAuthenticated={true}
        notifications={allNotifications}
      />
      <main className="main-content">
        <BreadcrumbTrail />
        
        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Analytics Dashboard</h1>
              <p className="text-muted-foreground">
                Comprehensive insights into user engagement and platform performance
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <TimeRangeFilter selectedRange={selectedRange} onRangeChange={handleRangeChange} />
              <Button variant="default" iconName="Download" iconPosition="left" onClick={handleExport} loading={isLoading}>
                Export Report
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiMetrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <EngagementChart
              data={engagementData}
              type="line"
              title="User Engagement Trends"
              dataKeys={[
                { dataKey: "logins", name: "Logins", color: "var(--color-primary)" },
                { dataKey: "quizzes", name: "Quizzes", color: "var(--color-accent)" },
                { dataKey: "interactions", name: "Interactions", color: "var(--color-secondary)" }
              ]}
            />
            <EngagementChart
              data={xpProgressData}
              type="bar"
              title="XP Earnings Progress"
              dataKeys={[
                { dataKey: "xp", name: "XP Earned", color: "var(--color-secondary)" }
              ]}
            />
          </div>

          <div className="mb-8">
            <ContentAnalytics 
              plays={popularPlays}
              quizzes={quizPerformance}
              glossaryTerms={glossaryTerms}
            />
          </div>

          <div className="mb-8">
            <UserActivityTable users={userData} onExport={handleExport} />
          </div>

          <div className="mb-8">
            <CohortAnalysis cohorts={cohortData} />
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="TrendingUp" size={24} className="text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-card-foreground mb-2">Key Insights</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>User engagement has increased by 18.2% compared to the previous period, indicating strong platform adoption.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Quiz completion rate of 87.3% demonstrates effective learning content and user motivation.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Romeo and Juliet remains the most popular play with 94% engagement, suggesting continued relevance in curriculum.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                    <span>Pronunciation quizzes show lower average scores (75%), indicating an opportunity for additional support materials.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;